
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';
import {AccountMargin} from '../core/AccountMargin';
import {FieldModelAccountMargin} from '../core/AccountMargin';
import {Asset} from '../core/Asset';
import {FieldModelAsset} from '../core/Asset';

/**
 * DepositAccountResponse struct
 */
class DepositAccountResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {AccountMargin=} margin
   * @param {Asset=} asset
   * @param {!string=} comment
   * @constructor
   */
  constructor (parent = new Response(), margin = null, asset = null, comment = '') {
    super()
    super.copy(parent)
    this.Margin = margin
    this.Asset = asset
    this.Comment = comment
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DepositAccountResponse}
   * @param {!DepositAccountResponse} other Other struct
   * @returns {!DepositAccountResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Margin != null) {
      this.Margin = AccountMargin.fromObject(other.Margin)
    } else {
      this.Margin = null
    }
    if (other.Asset != null) {
      this.Asset = Asset.fromObject(other.Asset)
    } else {
      this.Asset = null
    }
    if (other.Comment != null) {
      this.Comment = other.Comment
    } else {
      this.Comment = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DepositAccountResponse}
   * @returns {!DepositAccountResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DepositAccountResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DepositAccountResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DepositAccountResponse}
   * @param {!DepositAccountResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DepositAccountResponse)) {
      throw new TypeError('Instance of DepositAccountResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DepositAccountResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Margin: ((this.Margin != null) ? this.Margin : null),
      Asset: ((this.Asset != null) ? this.Asset : null),
      Comment: ((this.Comment != null) ? this.Comment : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DepositAccountResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DepositAccountResponse} other Object value
   * @returns {!DepositAccountResponse} Created struct
   */
  static fromObject (other) {
    return new DepositAccountResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DepositAccountResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DepositAccountResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DepositAccountResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6277
  }
}

export { DepositAccountResponse };

/**
 * Fast Binary Encoding DepositAccountResponse field model
 */
class FieldModelDepositAccountResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.Margin = new fbe.FieldModelOptional(new FieldModelAccountMargin(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.Asset = new fbe.FieldModelOptional(new FieldModelAsset(buffer, this.Margin.fbeOffset + this.Margin.fbeSize), buffer, this.Margin.fbeOffset + this.Margin.fbeSize)
    this.Comment = new fbe.FieldModelString(buffer, this.Asset.fbeOffset + this.Asset.fbeSize)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelDepositAccountResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelDepositAccountResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDepositAccountResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Margin.fbeSize + this.Asset.fbeSize + this.Comment.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDepositAccountResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Margin.fbeExtra + this.Asset.fbeExtra + this.Comment.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDepositAccountResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDepositAccountResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDepositAccountResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6277
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDepositAccountResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDepositAccountResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Margin.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Margin.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Margin.fbeSize

    if ((fbeCurrentSize + this.Asset.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Asset.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Asset.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Comment.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDepositAccountResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDepositAccountResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDepositAccountResponse}
   * @param {!DepositAccountResponse} fbeValue Default value, defaults is new DepositAccountResponse()
   * @returns {!DepositAccountResponse} DepositAccountResponse value
   */
  get (fbeValue = new DepositAccountResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDepositAccountResponse}
   * @param {!DepositAccountResponse} fbeValue DepositAccountResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Margin.fbeSize) <= fbeStructSize) {
      fbeValue.Margin = this.Margin.get()
    } else {
      fbeValue.Margin = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Margin.fbeSize

    if ((fbeCurrentSize + this.Asset.fbeSize) <= fbeStructSize) {
      fbeValue.Asset = this.Asset.get()
    } else {
      fbeValue.Asset = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Asset.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) <= fbeStructSize) {
      fbeValue.Comment = this.Comment.get()
    } else {
      fbeValue.Comment = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDepositAccountResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDepositAccountResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDepositAccountResponse}
   * @param {!DepositAccountResponse} fbeValue DepositAccountResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDepositAccountResponse}
   * @param {DepositAccountResponse} fbeValue DepositAccountResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Margin.set(fbeValue.Margin)
    this.Asset.set(fbeValue.Asset)
    this.Comment.set(fbeValue.Comment)
  }
}

export { FieldModelDepositAccountResponse };

/**
 * Fast Binary Encoding DepositAccountResponse model
 */
class DepositAccountResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDepositAccountResponse(this.buffer, 4)
  }

  /**
   * Get the DepositAccountResponse model
   * @this {!DepositAccountResponseModel}
   * @returns {!FieldModelDepositAccountResponse} model DepositAccountResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DepositAccountResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DepositAccountResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DepositAccountResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DepositAccountResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDepositAccountResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DepositAccountResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DepositAccountResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DepositAccountResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DepositAccountResponseModel}
   * @param {!DepositAccountResponse} value DepositAccountResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DepositAccountResponseModel}
   * @param {!DepositAccountResponse} value DepositAccountResponse value, defaults is new DepositAccountResponse()
   * @return {!object} Deserialized DepositAccountResponse value and its size
   */
  deserialize (value = new DepositAccountResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DepositAccountResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DepositAccountResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DepositAccountResponseModel}
   * @param {!number} prev Previous DepositAccountResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DepositAccountResponseModel };
